import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BidService } from '../../../services/bids.service';
import { ScreenSizeService } from '../../../services/screen-size.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService } from 'src/app/services/payment/user-wallet.service';
import { switchMap, of, throwError } from 'rxjs';

@Component({
  selector: 'app-editbid',
  templateUrl: './editbid.component.html',
  styleUrls: ['./editbid.component.css']
})
export class EditbidComponent implements OnInit {
  modalForm!: FormGroup;
  bids: any[] = [];
  activeBid: any | null = null;
  isMobile: boolean = false;
  isLoading: boolean = false;
  errorMessage: string = '';
  loadingMessage: string = '';

  constructor(
    private fb: FormBuilder,
    private bidService: BidService,
    private router: Router,
    private screenSizeService: ScreenSizeService,
    private paymentService: PaymentService,
    public dialogRef: MatDialogRef<EditbidComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.modalForm = this.fb.group({
      energy_amount: [parseFloat(this.data.active_bid.energy_amount), [Validators.required, Validators.min(0.01)]],
      bid_price: [parseFloat(this.data.active_bid.bid_price), [Validators.required, Validators.min(0.01)]]
    });
  }

  ngOnInit(): void {
    this.screenSizeService.isMobile$.subscribe(isMobile => {
      this.isMobile = isMobile;
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  get energy_amount() {
    return this.modalForm.get('energy_amount');
  }

  get bid_price() {
    return this.modalForm.get('bid_price');
  }

  submitBid() {
    if (this.modalForm.invalid) return;

    this.isLoading = true;
    this.errorMessage = '';
    this.loadingMessage = 'Validating balance...';

    const newEnergyAmount = this.modalForm.value.energy_amount;
    const newBidPrice = this.modalForm.value.bid_price;
    const transactionTypeId = this.data.active_bid.transaction_type === 'Buy' ? 1 : 2;

    this.paymentService.validateBalance({
      energy_amount: newEnergyAmount,
      bid_price: newBidPrice,
      bid_id: this.data.active_bid.id,
      transaction_type_id: transactionTypeId
    }).pipe(
      switchMap(res => {
        if (!res || typeof res.required_balance === 'undefined') {
          console.error('Invalid response from validateBalance:', res);
          return throwError(() => new Error('Invalid response from balance validation service.'));
        }

        const required = parseFloat(res.required_balance);

        if (required > 0) {
          // --- CASE 1: USER NEEDS TO PAY MORE ---
          this.loadingMessage = 'Creating payment intent...';
          return this.paymentService.addTokens({ amount: required, payment_method: 'icici_cbdc', device: 'web' });
        } else {
          // --- CASE 3: SUFFICIENT BALANCE; PROCEED TO UPDATE BID ---
          this.loadingMessage = 'Updating bid...';
          return this.bidService.updateBid({
            bid_round_id: this.data.active_bid.bid_round,
            bid_id: this.data.active_bid.id,
            energy_amount: newEnergyAmount,
            bid_price: newBidPrice
          }).pipe(
            switchMap(() => {
              this.dialogRef.close('success');
              return of({ bid_updated: true });
            })
          );
        }
      })
    ).subscribe({
      next: (response: any) => {
        if (response?.payment_intent) {
          const paymentContext = {
            intentLink: response.payment_intent.intent_link,
            txId: response.payment_intent.transaction_id,
            amount: response.payment_intent.amount,
            message: response.payment_intent.message,
            returnRoute: `/strike/${this.data.active_bid.id}`,
              qr_code: response.qr_code,
            editContext: {

              bid_id: this.data.active_bid.id,
              bid_round_id: this.data.active_bid.bid_round,
              energy_amount: newEnergyAmount,
              bid_price: newBidPrice
            },
            display: { title: 'Update Bid Payment' }
          };

          sessionStorage.setItem('pendingPaymentContext', JSON.stringify(paymentContext));
          this.dialogRef.close();
          this.router.navigate(['/payment'], { state: paymentContext });
        }
      },
      error: err => {
        console.error("Error during edit bid validation or update:", err);
        this.errorMessage = err?.error?.detail || err?.message || "An error occurred.";
        this.isLoading = false;
      }
    });
  }
}
